import { autoinject, bindable, transient, observable, computedFrom } from "aurelia-framework";
import { IPaginationPage } from '@rightship-tech/common-ui/components/comp-pagination/comp-pagination.models';
import { MyTasksService } from '../../../services/my-tasks.service';
import { QuoteService } from '../../../services/rightstore/quote.service';
import { LookupValue } from "@rightship-tech/common-ui/models/lookup.models";
import { CompListMenuItem } from "@rightship-tech/common-ui/components/comp-list-menu/comp-list-menu.models";
import { GetTasksRequest, TaskModel, TaskPriorityEnum } from "@rightship-tech/tasks-ui/models/tasks.models";
import { myVesselsDialogConstants } from "../../../constants/dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { MyVesselsRouteConstants } from "../../../constants/route.constants";
import { UserClaimsService } from '@rightship-tech/common-ui/services/basic/user-claims.service';
import { SignalClientService, SignalFunctions } from '@rightship-tech/common-ui/services/basic/signal-client.service';
import { CustomerService } from '@rightship-tech/common-ui/services/basic/customer-client.service';
import { UserClientService } from '@rightship-tech/common-ui/services/basic/user-client.service';
import { CreateRequestForQuote } from '../../../models/quote.models';
import { NotificationClientService, NotificationType } from "@rightship-tech/common-ui/services/basic/notification-client.service";
import { Claims } from '@rightship-tech/common-ui/constants/authentication/claims';
import { Router } from "aurelia-router";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { SelectVesselTypeEnum } from "../../../models/common.models";
import * as MyVesselsClaims from "../../../constants/claims.constants";

@autoinject
@transient()
export class PartialPendingTasks implements IPaginationPage {
	
	private isMobileFilterOpen: boolean;
	public lastPage: number = 12;
	public priorityPendingTasks: TaskModel[];
	public pendingTasks: TaskModel[];
	public loadingTasks: boolean = true;
	public fallbackTasks: boolean = false;
	public canAddVessel: boolean = false;
	public showDocumentRequirements: boolean = false;
	public menuItems: CompListMenuItem[] = [
		{
			item: "Submit missing PSC",
			click: () => this.submitMissingPsc(),
			classList: '',
			id: 'gtm-missing-psc-dropdown-select',
			isVisible: true,
		},
		{
			item: "Submit Class Status Report",
			click: () => this.submitClassStatusReport(),
			classList: '',
			isVisible: true,
		},
		{
			item: "Submit Change of Management",
			click: () => this.submitChangeOfManagement(),
			classList: '',
			isVisible: true,
		},
		{
			item: "Request RightShip Inspection",
			click: () => this.requestRightShipInspection(),
			classList: '',
			isVisible: true,
		}
	];
	private filter = {
		"priority": {
			"count": 0,
			"selected": false
		},
		"psc": {
			"count": 0,
			"selected": false
		},
		"inc": {
			"count": 0,
			"selected": false
		},
		"cfr": {
			"count": 0,
			"selected": false
		},
		"fbr": {
			"count": 0,
			"selected": false
		},
		"env": {
			"count": 0,
			"selected": false
		},
		"insp": {
			"count": 0,
			"selected": false
		},
		"forms": {
			"count": 0,
			"selected": false
		},
		"date": {
			"selected": false
		},
		"vessels": {
			"selected": [],
			"expanded": false
		},
		"mouDataVerification": {
			"count": 0,
			"selected": false
		},
		"documentRequirements": {
			"count": 0,
			"selected": false
		}
	}

	@bindable
	public vessels: LookupValue[];

	@bindable
	public vesselIdsFilter: number[];

	@bindable
	public selectedFilters: string[];

	@observable
	public currentPage: number = 1;

	constructor(private myTaskService: MyTasksService, private quoteClientService: QuoteService, private customerService: CustomerService,
		private userClientService: UserClientService, private userClaimsService: UserClaimsService,
		private dialogClientService: DialogClientService, private router: Router, private signalRService: SignalClientService,
		private notificationClientService: NotificationClientService) {

		//Claims
		this.canAddVessel = this.userClaimsService.hasPermission(Claims.CanAddVesselRightFLEETStarter);
		this.showDocumentRequirements = this.userClaimsService.hasPermission(MyVesselsClaims.Claims.ShowDocumentRequirementInHome);
	}

	attached() {

		//SignalR subscription
		this.signalRService.subscribe(SignalFunctions.onSyncTaskComplete,this.onTaskChanged.bind(this));

		//Filters
		this.filter.vessels.selected = this.vesselIdsFilter;

		//Set selected filters for the first and only time
		if(this.selectedFilters && this.selectedFilters.length > 0) {
			this.selectedFilters.forEach(filter => {
				this.filter[filter].selected = true;
			});
		}

		//Filter tasks
		this.bindCounts();
		this.filterTasks();
	}

	detached(){
		this.signalRService.unsubscribe(SignalFunctions.onSyncTaskComplete,this.onTaskChanged.bind(this));
	}

	onTaskChanged(params: { items: { entityTypeId: number, entityId: number }[] }) {
		var anyTaskModified = this.pendingTasks?.filter(x => {
			return params.items.filter(a => a.entityId == x.taskEntityId && x.taskType == a.entityTypeId).length > 0;
		}).length > 0
		var anyTaskModifiedPriority = this.priorityPendingTasks?.filter(x => {
			return params.items.filter(a => a.entityId == x.taskEntityId && x.taskType == a.entityTypeId).length > 0;
		}).length > 0

		if (anyTaskModified || anyTaskModifiedPriority) {
			this.bindCounts();
			this.filterTasks();
		}
	}

	vesselsChanged(newValue: LookupValue[], oldValue: LookupValue[]) {
		this.bindCounts();
		this.filterTasks();
	}

	priorityFilterClicked() {
		this.filter.priority.selected = !this.filter.priority.selected;
		this.filterTasks();
	}

	pscFilterClicked() {
		this.filter.psc.selected = !this.filter.psc.selected;
		this.filterTasks();
	}

	incFilterClicked() {
		this.filter.inc.selected = !this.filter.inc.selected;
		this.filterTasks();
	}

	cfrFilterClicked(refresh: boolean = true) {
		this.filter.cfr.selected = !this.filter.cfr.selected;
		if (refresh) this.filterTasks();
	}

	fbrFilterClicked(refresh: boolean = true) {
		this.filter.fbr.selected = !this.filter.fbr.selected;
		if (refresh) this.filterTasks();
	}

	envFilterClicked(refresh: boolean = true) {
		this.filter.env.selected = !this.filter.env.selected;
		if (refresh) this.filterTasks();
	}

	inspFilterClicked(refresh: boolean = true) {
		this.filter.insp.selected = !this.filter.insp.selected;
		if (refresh) this.filterTasks();
	}

	formsFilterClicked(refresh: boolean = true) {
		this.filter.forms.selected = !this.filter.forms.selected;
		if (refresh) this.filterTasks();
	}

	mouDataVerificationFilterClicked(refresh: boolean = true) {
		this.filter.mouDataVerification.selected = !this.filter.mouDataVerification.selected;
		if (refresh) this.filterTasks();
	}

	documentRequirementsFilterClicked(refresh: boolean = true) {
		this.filter.documentRequirements.selected = !this.filter.documentRequirements.selected;
		if (refresh) this.filterTasks();
	}

	//Returns true if any of the filters are selected
	@computedFrom("filter.priority.selected", "filter.psc.selected", "filter.inc.selected", "filter.vessels.selected.length")
	get isFiltered() {
		return this.filter.priority.selected || this.filter.psc.selected || this.filter.inc.selected || this.filter.vessels.selected.length > 0;
	}

	//Clear all filters
	clearAllFilters() {
		this.filter.priority.selected = false;
		this.filter.psc.selected = false;
		this.filter.inc.selected = false;
		this.filter.cfr.selected = false;
		this.filter.fbr.selected = false;
		this.filter.insp.selected = false;
		this.filter.env.selected = false;
		this.filter.forms.selected = false;
		this.filter.mouDataVerification.selected = false;
		this.filter.documentRequirements.selected = false;
		this.filter.vessels.selected = [];
		this.filterTasks();
	}

	//Filter the tasks based on the selected filters
	async filterTasks(resetPage: boolean = true) {
		this.filter.vessels.expanded = false;
		if (resetPage) this.currentPage = 1;

		let request: GetTasksRequest = {
			vessels: this.vessels?.map((vessel: LookupValue) => vessel.id),
			statuses: [],
			rowPerPage: 12,
			priority: TaskPriorityEnum.Standard, //Standard
			page: this.currentPage,
			orderBy: "ASC",
			sortBy: "Date"
		};

		if (this.filter.priority.selected) {
			request.priority = 1;
		}

		if (this.filter.vessels.selected.length > 0) {
			request.vessels = this.filter.vessels.selected;
		}
		else {
			request.vessels = this.vessels?.map((vessel: LookupValue) => vessel.id);
		}

		request.types = [];
		if (this.filter.psc.selected) {
			request.types.push(EntityTypes.PSC);
		}
		if (this.filter.inc.selected) {
			request.types.push(EntityTypes.Incident);
		}
		if (this.filter.cfr.selected) {
			request.types.push(EntityTypes.Cfr);
		}
		if (this.filter.fbr.selected) {
			request.types.push(EntityTypes.FBR);
		}
		if (this.filter.env.selected) {
			request.types.push(EntityTypes.GHGVerification);
		}
		if (this.filter.insp.selected) {
			request.types.push(EntityTypes.RightShipInspection);
		}
		if (this.filter.forms.selected) {
			request.types.push(EntityTypes.TVQ);
		}
		if (this.filter.mouDataVerification.selected) {
			request.mouDataVerification = true;
		}
		if (this.filter.documentRequirements.selected) {
			request.types.push(EntityTypes.DocumentRequirementSubsciptionVessels);
		}
		this.loadingTasks = true;
		this.fallbackTasks = false;

		//Get pending tasks
		if (request.vessels?.length > 0) {

			//get standard pending tasks with paging
			request.priority = this.filter.priority.selected ? TaskPriorityEnum.Priority : null;
			request.prioritized = false;
			let response = await this.myTaskService.getPendingTask(request);
			if (response?.result != null) {
				this.pendingTasks = response.result;
				this.lastPage = response?.totalPage;
			}

			//get priority pending tasks without paging
			request.priority = this.filter.priority.selected ? TaskPriorityEnum.Priority : null;
			request.prioritized = true;
			request.page = null; // not paging
			let response2 = await this.myTaskService.getPendingTask(request);

			if (response2?.result != null) {
				this.priorityPendingTasks = response2.result;
				this.filter.priority.count = response2.result.filter((task: TaskModel) => task.priority == TaskPriorityEnum.Priority).length;
			}

			if ((!this.pendingTasks || this.pendingTasks?.length == 0) && (!this.priorityPendingTasks || this.priorityPendingTasks?.length == 0)) {
				this.fallbackTasks = true;
			}

			this.loadingTasks = false;
		}
		else {
			this.loadingTasks = false;
			this.fallbackTasks = true;
			this.filter.priority.count = 0;
		}
	}

	async bindCounts() {
		let request: GetTasksRequest = {
			vessels: this.vessels?.map((vessel: LookupValue) => vessel.id),
			statuses: [],
			rowPerPage: 1,
			priority: null,
			page: 1,
			getCountByType: true,
			orderBy: "ASC",
			sortBy: "Date"
		};
		let response = await this.myTaskService.getPendingTask(request);
		if (response?.countByTypes['PSC']) {

			this.filter.psc.count = parseInt(response.countByTypes['PSC']);
		}
		if (response?.countByTypes['INC']) {

			this.filter.inc.count = parseInt(response.countByTypes['INC']);
		}
		if (response?.countByTypes['CFR']) {

			this.filter.cfr.count = parseInt(response.countByTypes['CFR']);
		}
		if (response?.countByTypes['FBR']) {

			this.filter.fbr.count = parseInt(response.countByTypes['FBR']);
		}
		if (response?.countByTypes['EnvironmentalReview']) {

			this.filter.env.count = parseInt(response.countByTypes['EnvironmentalReview']);
		}
		if (response?.countByTypes['RightShipInspection']) {

			this.filter.insp.count = parseInt(response.countByTypes['RightShipInspection']);
		}
		if (response?.countByTypes['Forms']) {

			this.filter.forms.count = parseInt(response.countByTypes['Forms']);
		}
		if (response?.countByTypes['MOU Data Verification']) {

			this.filter.mouDataVerification.count = parseInt(response.countByTypes['MOU Data Verification']);
		}
		if (response?.countByTypes['DocumentRequirements']) {

			this.filter.documentRequirements.count = parseInt(response.countByTypes['DocumentRequirements']);
		}
	}

	//Open inspection form
	async requestRightShipInspection() {
		try {
			let businessUnitId = await this.customerService.getBusinessUnitId();
			let request: CreateRequestForQuote = {
				webOrderNumber: null,
				userName: this.userClientService.currentUser?.user.email || null,
				customerNumber:  businessUnitId || null,
				quoteLines: [{
					productNumber: "rightship-inspection-booking",
					status: 1,
					queryParams: ""
				}],
			};
			let response = await this.quoteClientService.getQuote(request);
			if(response?.quoteLines?.length > 0) window.open(response.quoteLines[0].url, "_blank");
		} catch (e) {
            this.notificationClientService.show("Error!", "Request RightShip Inspection Failed", NotificationType.Error);
		}
	}

	addVessel() {
		this.dialogClientService.show(myVesselsDialogConstants.addVesselDialog, {
			onVesselAdded: (() => {
				this.bindCounts();
				this.filterTasks();
			}).bind(this)
		}, true, false, false, "right");
	}

	currentPageChanged(newValue: number, oldValue: number) {
		this.filterTasks(false);
	}

	nextClick() {

	};

	previousClick() {

	};

	//Toggle mobile filter dialog
	public toggleMobileFilterDialog() {
		this.isMobileFilterOpen = !this.isMobileFilterOpen;
	}

	//Hide mobile filter
	public hideMobileFilters() {
		this.isMobileFilterOpen = false;
	}

	//Apply all filters which where selected on mobile
	public mobileApplyAllFilters() {
		this.filterTasks();
		this.hideMobileFilters();
	}

	//Apply selected filters which where selected on mobile
	public mobileApplySelectedFilters() {

	}

	//Submit missing PSC
	private submitMissingPsc() {
		this.router.navigateToRoute(MyVesselsRouteConstants.SelectVessel, 
			{ 
				title: "Add Missing PSC", 
				subTitle: "Select vessel you would like to create PSC for", 
				type: SelectVesselTypeEnum.MissingPsc 
			});
	}

	//Submit class status report
	private submitClassStatusReport() {
		this.router.navigateToRoute(MyVesselsRouteConstants.SelectVessel, 
			{ 
				title: "Submit Class Status Report", 
				subTitle: "Select vessel you would like to submit class status report for", 
				type: SelectVesselTypeEnum.ClassStatusReport 
			});
	}

	//Submit change of management
	private submitChangeOfManagement() {
		this.router.navigateToRoute(MyVesselsRouteConstants.SelectVessel, 
			{ 
				title: "Submit Management of Change (MoC) / Vessel Take Over Process (VTOP) Questionnaire", 
				subTitle: "Select vessel you would like to submit MoC / VTOP questionnaire for", 
				type: SelectVesselTypeEnum.ChangeOfManagement 
			});
	}
}